/**
 * Translation Helper
 * 
 * Utility functions for accessing translations in TypeScript
 * Translations are provided by PHP via wp_localize_script() and available in window.gtaTranslations
 */

// Declare global type for translations
declare global {
    interface Window {
        gtaTranslations?: {
            // Error messages
            error_validation?: string;
            error_api?: string;
            error_network?: string;
            error_subscription?: string;
            
            // Success messages
            success_subscription?: string;
            
            // UI labels
            label_tracking_history?: string;
            label_shipment_details?: string;
            label_show_tracking_history?: string;
            label_hide_tracking_history?: string;
            label_show_shipment_details?: string;
            label_hide_shipment_details?: string;
            
            // Status labels
            status_delivered?: string;
            status_in_transit?: string;
            status_out_for_delivery?: string;
            status_unknown?: string;
            
            // Progress labels
            progress_label_created?: string;
            progress_received?: string;
            progress_in_transit?: string;
            progress_out_for_delivery?: string;
            progress_delivered?: string;
            
            // Detail labels
            detail_origin?: string;
            detail_destination?: string;
            detail_shipper?: string;
            detail_consignee?: string;
            detail_service?: string;
            detail_status?: string;
            detail_terminal?: string;
            detail_weight?: string;
            detail_created_date?: string;
            detail_ship_date?: string;
            detail_delivery_date?: string;
            detail_total_pieces?: string;
            detail_reference?: string;
            
            // Not found messages
            not_found_title?: string;
            not_found_description?: string;
            not_found_reasons_title?: string;
            not_found_reason_1?: string;
            not_found_reason_2?: string;
            not_found_reason_3?: string;
            not_found_reason_4?: string;
            not_found_try_another?: string;
            not_found_contact_support?: string;
            
            // Proof of delivery
            pod_title?: string;
            pod_postal_code_label?: string;
            pod_postal_code_placeholder?: string;
            pod_postal_code_button?: string;
            pod_invalid_postal?: string;
            pod_enter_postal?: string;
            pod_current_status?: string;
            pod_delivered_date?: string;
            pod_download_attachments?: string;
            pod_click_to_download?: string;
            pod_image_number?: string;
            
            // Other UI
            label_last_updated?: string;
            label_status?: string;
            label_tracking_number?: string;
            label_estimated_delivery?: string;
            label_before_9pm?: string;
            label_location?: string;
            label_support?: string;
            
            // Validation
            validation_tracking_too_short?: string;
            validation_tracking_too_long?: string;
            validation_tracking_invalid_chars?: string;
            validation_tracking_empty?: string;
            validation_max_tracking?: string;
            validation_at_least_one?: string;
            
            // Language info
            _language?: {
                code: string;
                name: string;
                available_languages: Record<string, any>;
            };
        };
    }
}

/**
 * Translation Helper Class
 */
export class TranslationHelper {
    /**
     * Get the current language from URL parameter, HTML lang attribute, or window object
     */
    public static getCurrentLang(): string {
        // Check for force_lang URL parameter for testing
        const urlParams = new URLSearchParams(window.location.search);
        const forceLang = urlParams.get('force_lang');
        if (forceLang) {
            if (forceLang === 'fr' || forceLang === 'french') {
                return 'fr_FR';
            } else if (forceLang === 'en' || forceLang === 'english') {
                return 'en_US';
            }
        }
        
        const htmlLang = document.documentElement.lang || 'en-US';
        // Convert language codes: fr -> fr_FR, en -> en_US, etc.
        const langMap: Record<string, string> = {
            'fr': 'fr_FR',
            'fr-FR': 'fr_FR',
            'es': 'es_ES',
            'es-ES': 'es_ES',
            'de': 'de_DE',
            'de-DE': 'de_DE',
            'it': 'it_IT',
            'it-IT': 'it_IT',
            'pt': 'pt_BR',
            'pt-BR': 'pt_BR',
            'nl': 'nl_NL',
            'nl-NL': 'nl_NL',
            'ja': 'ja',
            'zh': 'zh_CN',
            'zh-CN': 'zh_CN',
            'ko': 'ko_KR',
            'ko-KR': 'ko_KR',
            'en': 'en_US',
            'en-US': 'en_US'
        };
        return langMap[htmlLang] || langMap[htmlLang.split('-')[0]] || 'en_US';
    }
    
    /**
     * Get translation by key with fallback
     * @param key Translation key
     * @param fallback Fallback text if translation not found
     * @returns Translated text or fallback
     */
    public static t(key: string, fallback: string): string {
        // Check for new multi-language format
        const allTranslations = (window as any).gtaTranslationsAll;
        if (allTranslations) {
            const currentLang = this.getCurrentLang();
            const langTranslations = allTranslations[currentLang];
            if (langTranslations && langTranslations[key]) {
                return langTranslations[key];
            }
            // Fallback to English if current language doesn't have the translation
            if (allTranslations['en_US'] && allTranslations['en_US'][key]) {
                return allTranslations['en_US'][key];
            }
        }
        
        // Fallback to old format (for backwards compatibility)
        const translations = window.gtaTranslations;
        if (translations && (translations as any)[key]) {
            return (translations as any)[key];
        }
        
        return fallback;
    }
    
    /**
     * Format translation with parameters
     * @param key Translation key
     * @param params Parameters to replace %s, %d, etc.
     * @param fallback Fallback text
     * @returns Formatted translated text
     */
    public static tf(key: string, params: (string | number)[], fallback: string): string {
        let text = this.t(key, fallback);
        
        // Replace %d with numbers, %s with strings
        params.forEach((param) => {
            const pattern = typeof param === 'number' ? '%d' : '%s';
            text = text.replace(pattern, String(param));
        });
        
        return text;
    }
    
    /**
     * Check if translations are loaded
     * @returns True if translations object exists
     */
    public static isLoaded(): boolean {
        return !!window.gtaTranslations;
    }
    
    /**
     * Get current language name
     * @returns Language name (e.g., 'French', 'English')
     */
    public static getCurrentLanguageName(): string {
        return window.gtaTranslations?._language?.name || 'English';
    }
    
    /**
     * Check if current language is French
     * @returns True if French is active
     */
    public static isFrench(): boolean {
        return this.getCurrentLang().startsWith('fr');
    }
}

// Export convenience functions
export const t = TranslationHelper.t;
export const tf = TranslationHelper.tf;

/**
 * Usage Examples:
 * 
 * // Simple translation
 * import { t } from '@/utils/TranslationHelper';
 * const message = t('error_validation', 'Validation error');
 * 
 * // Translation with parameters
 * import { tf } from '@/utils/TranslationHelper';
 * const message = tf('validation_tracking_too_short', [5], 'Tracking number 5 is too short');
 * 
 * // Using the class
 * import { TranslationHelper } from '@/utils/TranslationHelper';
 * if (TranslationHelper.isFrench()) {
 *     console.log('Interface is in French');
 * }
 */

